import axios from "axios";
import { clearUser } from "../../store/auth/userSlice";
import {
	setLoadingClients,
	setSelectedClient,
} from "../../store/modules/accountManagement/clientsSlice";

const handleUnauthorized = (dispatch, navigate) => {
	dispatch(clearUser());
	navigate("/login");
};

export const fetchClients = async (
	token,
	dispatch,
	navigate,
	page,
	perPage,
	searchTerms,
	paginate = 1,
	signal = null,
) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
		params: {
			page: page,
			per_page: perPage,
			name: searchTerms?.name || searchTerms?.filter || null,
			distributor_id: searchTerms?.distributor_id || null,
			clientNumber: searchTerms?.clientNumber || null,
			businessUnit: searchTerms?.businessUnit || null,
			selected: searchTerms?.selected || null,
			businessUnits: searchTerms?.businessUnits || null,
			distributor: searchTerms?.distributor || null,
			reseller: searchTerms?.reseller || null,
			reseller_id: searchTerms?.reseller_id || null,
			paginate: paginate,
			accountNumber: Array.isArray(searchTerms?.accountNumber)
				? searchTerms.accountNumber
				: [searchTerms.accountNumber],
			search: searchTerms?.search || null,
		},
		signal: signal,
	};
	try {
		const response = await axios.get(`${import.meta.env.VITE_API_BACKEND}api/clients`, config);
		let data;

		if (!response.data.data || response.data.data.length === 0) {
			data = {
				data: [],
				totalItems: 0,
				page: paginate === 1 ? page : 1,
				per_page: paginate === 1 ? perPage : 10,
			};
		} else {
			const totalItems = response.data?.total;
			data = {
				data: response.data.data,
				totalItems: totalItems,
				page: paginate === 1 ? page : 1,
				perPage: paginate === 1 ? perPage : 10,
			};
		}
		return data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching client data:", error);
		}
	}
};

export const createClient = async (data, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	};

	try {
		const response = await axios.post(
			`${import.meta.env.VITE_API_BACKEND}api/clients`,
			data,
			config,
		);
		return response.data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else if (error.response && error.response.status === 400) {
			const serverMessage = error.response.data.message || "Unexpected error.";
			return { errors: serverMessage };
		} else if (error.response && error.response.status === 404) {
			const serverMessage = error.response.data.message || "Unexpected error.";
			return { errors: serverMessage };
		} else if (error.response && error.response.status === 422) {
			return { errors: error.response.data.errors };
		} else {
			console.error("Error creating client:", error);
		}
		return null;
	}
};

export const updateClient = async (id, data, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	};
	try {
		const response = await axios.put(
			`${import.meta.env.VITE_API_BACKEND}api/clients/${id}`,
			data,
			config,
		);
		return response.data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else if (error.response && error.response.status === 400) {
			const serverMessage = error.response.data.message || "Unexpected error.";
			return { errors: serverMessage };
		} else if (error.response && error.response.status === 404) {
			const serverMessage = error.response.data.message || "Unexpected error.";
			return { errors: serverMessage };
		} else if (error.response && error.response.status === 422) {
			return { errors: error.response.data.errors };
		} else {
			console.error("Error updating client:", error);
		}
		return null;
	}
};

export const deleteClientById = async (id, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	};
	try {
		const response = await axios.delete(
			`${import.meta.env.VITE_API_BACKEND}api/clients/${id}`,
			config,
		);
		if (response.status === 200 || response.status === 202 || response.status === 204) {
			return {
				status: "success",
				message: "Client deleted successfully.",
			};
		} else {
			return {
				status: "error",
				message: response.data?.message || "An unexpected error occurred.",
			};
		}
	} catch (error) {
		if (error.response) {
			switch (error.response.status) {
				case 401:
					handleUnauthorized(dispatch, navigate);
					break;
				case 404:
					return { status: "not_found", message: "Not found." };
				case 405:
					return {
						status: "method_not_allowed",
						message: "Method Not Allowed",
					};
				case 400:
					return {
						status: "error",
						message: error.response.data?.message || "An error occurred.",
					};
				default:
					return { status: "error", message: "An error occurred." };
			}
		} else {
			return { status: "error", message: "Network error" };
		}
	}
};

export const fetchAllClients = async (token, dispatch, navigate, searchTerms) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
		params: {
			businessUnits: searchTerms?.businessUnits || null,
			distributor: searchTerms?.distributor || null,
			reseller: searchTerms?.reseller || null,
		},
	};

	try {
		const response = await axios.get(`${import.meta.env.VITE_API_BACKEND}api/clients`, config);

		let data;

		if (!response.data || response.data.length === 0) {
			data = {
				data: [],
				totalItems: 0,
			};
		} else {
			const totalItems = response.data?.total;
			data = {
				data: response.data,
				totalItems: totalItems,
			};
		}

		return data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching client data:", error);
		}
	}
};

export const fetchClientById = async (id, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	};
	try {
		const response = await axios.get(
			`${import.meta.env.VITE_API_BACKEND}api/clients/${id}`,
			config,
		);
		return response.data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			console.error("Unauthorized error, token might be invalid:", error.response);
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching client data:", error);
		}
	}
};

export const fetchClientDetails = async (id, token, dispatch, navigate) => {
	await dispatch(setSelectedClient(id));

	try {
		const data = await fetchClientById(id, token, dispatch, navigate);
		dispatch(setSelectedClient(data));
	} catch (error) {
		console.error("Error fetching data:", error);
	} finally {
		dispatch(setLoadingClients(false));
	}
};

export const fetchClientUsers = async (
	token,
	clientId,
	dispatch,
	navigate,
	page = 1,
	perPage = 10,
	searchTerm = "",
	paginate = 1,
	signal = null,
) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
		params: {
			client_id: clientId,
			page,
			per_page: perPage,
			search: searchTerm,
			paginate,
		},
		signal,
	};
	try {
		const response = await axios.get(
			`${import.meta.env.VITE_API_BACKEND}api/clients/${clientId}/users`,
			config,
		);

		const apiData = response.data.users;
		const paginationResponse = apiData.meta;

		const { users, total, current_page, per_page } = response.data;

		const data =
			users.length === 0
				? {
						data: [],
						totalItems: 0,
						page: current_page,
						perPage: per_page,
					}
				: {
						data: users,
						totalItems: total,
						page: current_page,
						perPage: per_page,
					};

		return data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching client users:", error);
		}
	}
};
